import { msg } from "@lingui/core/macro";
/**
 * ConnectorsIntro — the "what are connectors / how do they work" explainer for
 * the Integrations page. Split into two pieces so the hub can place the
 * trigger in its compact meta row and let the content expand INLINE below,
 * instead of spending a full-width collapsed bar at the top:
 *
 *   - ``ConnectorsIntroToggle`` — the light "Come funzionano?" link (goes in
 *     the header/meta row).
 *   - ``ConnectorsIntro``       — the expandable content panel (renders only
 *     when open).
 *
 * Both share the persisted ``sectionGuideStore`` key, collapsed by default —
 * the hub's job is connecting sources, so the explainer starts tucked away.
 * On-brand, Lucide-only, fully translatable.
 */
import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown, FolderOpen, Landmark, Plug, ShieldCheck, Sparkles } from "lucide-react";
import { useEffect } from "react";

import { cn } from "@ando/ui";

import { useSectionGuideStore } from "./sectionGuideStore";

const COLLAPSE_KEY = "connectors-intro";

interface CategoryDef {
  readonly id: string;
  readonly icon: typeof Plug;
  readonly title: ReturnType<typeof msg>;
  /** Localizable — keeps proper nouns but lets descriptive words translate. */
  readonly examples: ReturnType<typeof msg>;
}

const CATEGORIES: readonly CategoryDef[] = [
  {
    id: "productivity",
    icon: Sparkles,
    title: msg`Produttività`,
    examples: msg`Gmail, Outlook, Google Calendar, Slack, Teams`,
  },
  {
    id: "knowledge",
    icon: FolderOpen,
    title: msg`Conoscenza`,
    examples: msg`Google Drive, OneDrive, SharePoint, file locali/NAS`,
  },
  {
    id: "finance",
    icon: Landmark,
    title: msg`Amministrazione & Finance`,
    examples: msg`Gestionale/ERP, fatturazione elettronica, open banking`,
  },
  {
    id: "compliance",
    icon: ShieldCheck,
    title: msg`Compliance (Italia)`,
    examples: msg`PEC, SDI, cassetto fiscale, conservazione a norma`,
  },
];

/** Read the persisted collapsed flag (collapsed by default). */
function useIntroCollapsed(): boolean {
  return useSectionGuideStore((s) => s.collapsed[COLLAPSE_KEY] ?? true);
}

/**
 * The light "Come funzionano?" trigger — sits in the hub's meta row.
 * Seeds the store once so the FIRST click opens (the shared ``toggle`` flips
 * ``!collapsed[key]``; without a stored value it would no-op on click one).
 */
export function ConnectorsIntroToggle(): React.ReactElement {
  const collapsed = useIntroCollapsed();
  const toggle = useSectionGuideStore((s) => s.toggle);
  useEffect(() => {
    const st = useSectionGuideStore.getState();
    if (st.collapsed[COLLAPSE_KEY] === undefined) st.toggle(COLLAPSE_KEY);
  }, []);
  return (
    <button
      type="button"
      onClick={() => toggle(COLLAPSE_KEY)}
      aria-expanded={!collapsed}
      aria-controls="connectors-intro-panel"
      className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded font-medium text-[12px] text-ink-2 transition-colors hover:text-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
    >
      <Trans>Come funzionano</Trans>
      <ChevronDown
        size={14}
        strokeWidth={2}
        aria-hidden
        className={cn("transition-transform", !collapsed && "rotate-180")}
      />
    </button>
  );
}

/** The expandable explainer panel — renders only when open. */
export function ConnectorsIntro(): React.ReactElement {
  const { i18n } = useLingui();
  const collapsed = useIntroCollapsed();
  const reduce = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {collapsed ? null : (
        <motion.div
          key="connectors-intro"
          id="connectors-intro-panel"
          initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
          animate={
            reduce
              ? { opacity: 1, transition: { duration: 0.15 } }
              : {
                  height: "auto",
                  opacity: 1,
                  transition: {
                    // Fluid decel (easeOutExpo-like) on expand; opacity fades
                    // in just after the height starts so content doesn't pop.
                    height: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
                    opacity: { duration: 0.24, ease: "easeOut", delay: 0.05 },
                  },
                }
          }
          exit={
            reduce
              ? { opacity: 0, transition: { duration: 0.1 } }
              : {
                  height: 0,
                  opacity: 0,
                  transition: {
                    // Collapse accelerates into 0 (no decelerating tail = no
                    // end "lug"); opacity leads out fast so the last px are
                    // already invisible when the height lands.
                    height: { duration: 0.22, ease: [0.4, 0, 1, 1] },
                    opacity: { duration: 0.1, ease: "linear" },
                  },
                }
          }
          className="overflow-hidden"
        >
          <div className="rounded-xl border border-hairline bg-cream-deep/40 p-4">
            <div className="flex flex-col gap-4">
              <p className="max-w-3xl text-[13px] text-ink-2 leading-relaxed">
                <Trans>
                  I connettori collegano i tuoi strumenti ad Ando. Una volta connessi, Ando
                  indicizza e tiene aggiornata la conoscenza aziendale in un unico Company Brain:
                  puoi interrogare in linguaggio naturale tra tutti i sistemi, ricevere briefing e
                  far eseguire azioni — sempre con la tua approvazione.
                </Trans>
              </p>

              {/* Categories */}
              <div className="grid gap-2.5 sm:grid-cols-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <div
                      key={c.id}
                      className="flex items-start gap-2.5 rounded-lg border border-hairline bg-cream-deep/40 px-3 py-2.5"
                    >
                      <Icon
                        size={15}
                        strokeWidth={1.75}
                        aria-hidden
                        className="mt-0.5 shrink-0 text-ando-orange"
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-[12.5px] text-ink-1">{i18n._(c.title)}</p>
                        <p className="text-[11.5px] text-ink-3 leading-relaxed">
                          {i18n._(c.examples)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Security + how-to */}
              <div className="flex flex-col gap-1.5">
                <p className="flex items-start gap-2 text-[12px] text-ink-2 leading-relaxed">
                  <ShieldCheck
                    size={13}
                    strokeWidth={1.75}
                    aria-hidden
                    className="mt-0.5 shrink-0 text-ink-3"
                  />
                  <Trans>
                    Connessione via OAuth sicuro, token cifrati e revocabili in qualsiasi momento:
                    Ando accede solo a ciò che autorizzi.
                  </Trans>
                </p>
                <p className="flex items-start gap-2 text-[12px] text-ink-2 leading-relaxed">
                  <Plug
                    size={13}
                    strokeWidth={1.75}
                    aria-hidden
                    className="mt-0.5 shrink-0 text-ink-3"
                  />
                  <Trans>
                    Per collegare: scegli un connettore qui sotto, premi Collega e autorizza nella
                    finestra del provider. Stato e ultimo aggiornamento sono mostrati su ogni riga.
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

ConnectorsIntro.displayName = "ConnectorsIntro";
